import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
// The package has no root export, only ./node, ./browser and ./universal. The
// browser entry is the one that produces a Blob without touching fs.
import writeXlsxFile from 'write-excel-file/browser';
import { sanitizeRowsForExport } from '../utils/export-sanitize';

const EXCEL_EXTENSION = '.xlsx';

// Minimum and maximum column widths, in characters. The original xlsx-based
// implementation sized columns to the widest cell with no ceiling, which made a
// single long free-text comment blow a column out to hundreds of characters.
const MIN_COL_WIDTH = 8;
const MAX_COL_WIDTH = 60;
const NUMERIC_COL_WIDTH = 16;

/*
 * Writes .xlsx exports.
 *
 * Uses write-excel-file rather than xlsx (SheetJS). SheetJS stopped publishing to
 * npm after 0.18.5, so the registry copy is permanently stuck behind five
 * advisories (including prototype pollution, CVSS 7.8) with no possible upgrade
 * path. They were not reachable here because this service only ever wrote files
 * and never parsed them, but an unpatchable dependency is a liability the moment
 * anyone adds a read path.
 *
 * exceljs was evaluated and rejected: it pulls in archiver -> readdir-glob ->
 * glob -> minimatch -> brace-expansion and would have reintroduced the exact
 * advisory cascade that removing karma had just eliminated. write-excel-file has
 * a single dependency (fflate).
 */
@Injectable()
export class ExcelService {

    public async exportAsExcelFile(rawJson: unknown[], excelFileName: string): Promise<void> {
        // Neutralize spreadsheet formula injection before anything reads the
        // values. Applied here so every caller of this service inherits it.
        const rows = (sanitizeRowsForExport(rawJson) || []) as Record<string, unknown>[];

        const headers = this.collectHeaders(rows);
        if (!headers.length) {
            return;
        }

        // write-excel-file takes a grid of cell objects: first row the header,
        // then one row per record, in header order.
        const grid = [
            headers.map(header => ({ value: header, fontWeight: 'bold' as const, type: String })),
            ...rows.map(row => headers.map(header => this.toCell(row[header])))
        ];

        const columns = headers.map(header => ({ width: this.columnWidth(header, rows) }));

        // writeXlsxFile returns { toBlob, toFile } synchronously; toBlob is the
        // async part. Going through a Blob rather than toFile() keeps the
        // existing "<name>_export_<timestamp>.xlsx" naming via FileSaver.
        const blob = await writeXlsxFile(grid as never, { columns }).toBlob();
        this.saveAsExcelFile(blob, excelFileName);
    }

    // Records are not guaranteed to share a key set, and the previous
    // implementation silently assumed they did: it indexed column widths by
    // position across Object.values(), so a row with a different key order
    // produced mismatched widths. Collecting the union in first-seen order keeps
    // every column and a stable order.
    private collectHeaders(rows: Record<string, unknown>[]): string[] {
        const headers: string[] = [];
        rows.forEach(row => {
            if (row && typeof row === 'object') {
                Object.keys(row).forEach(key => {
                    if (!headers.includes(key)) {
                        headers.push(key);
                    }
                });
            }
        });
        return headers;
    }

    // Numbers are written as numbers so they stay summable in Excel. This is the
    // other half of the formula-injection defence in export-sanitize.ts, which
    // deliberately leaves plain numeric strings unquoted for the same reason.
    private toCell(value: unknown): { value: unknown; type: unknown } {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return { value, type: Number };
        }
        if (typeof value === 'boolean') {
            return { value, type: Boolean };
        }
        if (value instanceof Date) {
            return { value, type: Date };
        }
        return { value: value === null || value === undefined ? '' : String(value), type: String };
    }

    private columnWidth(header: string, rows: Record<string, unknown>[]): number {
        let width = header.length;
        for (const row of rows) {
            const value = row ? row[header] : undefined;
            if (typeof value === 'number') {
                width = Math.max(width, NUMERIC_COL_WIDTH);
            } else if (value !== null && value !== undefined) {
                width = Math.max(width, String(value).length);
            }
        }
        return Math.min(Math.max(width, MIN_COL_WIDTH), MAX_COL_WIDTH);
    }

    private saveAsExcelFile(blob: Blob, fileName: string): void {
        FileSaver.saveAs(blob, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    }
}
